import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import ar from './locales/ar.json';
import bn from './locales/bn.json';
import de from './locales/de.json';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import hi from './locales/hi.json';
import id from './locales/id.json';
import ptBR from './locales/pt-BR.json';
import ru from './locales/ru.json';
import zhHans from './locales/zh-Hans.json';

/// The ten most spoken languages plus German. English is the source and the key language;
/// every user-facing string goes through here, none are hardcoded in a component.
export const supportedLanguages = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'zh-Hans', label: '简体中文' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'pt-BR', label: 'Português (Brasil)' },
  { code: 'ru', label: 'Русский' },
  { code: 'id', label: 'Bahasa Indonesia' },
] as const;

export type LanguageCode = (typeof supportedLanguages)[number]['code'];

/// Languages written right to left. MUI is switched to RTL for these, and the document
/// direction follows - which is why no layout may hardcode left or right.
const rightToLeftLanguages = new Set<string>(['ar']);

export function isRightToLeft(language: string): boolean {
  return rightToLeftLanguages.has(language.split('-')[0]);
}

await i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      de: { translation: de },
      'zh-Hans': { translation: zhHans },
      hi: { translation: hi },
      es: { translation: es },
      fr: { translation: fr },
      ar: { translation: ar },
      bn: { translation: bn },
      'pt-BR': { translation: ptBR },
      ru: { translation: ru },
      id: { translation: id },
    },
    fallbackLng: 'en',
    supportedLngs: supportedLanguages.map((language) => language.code),
    // Keys are flat and contain dots, for example "nav.dashboard".
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false },
    // Plugin catalogues are added after start-up, once there is someone signed in to fetch
    // them for. Without this a component would keep showing the raw keys until the language
    // changed.
    react: { bindI18nStore: 'added' },
  });

/// The full key a plugin-relative key is served under. Plugins ship their own catalogues, and
/// the server namespaces every key with the plugin id so a plugin cannot ship a key the server
/// itself uses and quietly relabel the application around it.
export function pluginKey(pluginId: string, key: string): string {
  return `plugin.${pluginId}.${key}`;
}

/// Languages whose plugin catalogue has already been fetched, so switching back and forth does
/// not ask the server again.
const loadedPluginLanguages = new Set<string>();

/// Merges the plugin catalogues for a language into the running instance. Called after signing
/// in and whenever the language changes; a failure is not fatal, the affected labels simply
/// stay as their keys and the plugin pages still work.
export async function loadPluginTranslations(
  language: string,
  fetchBundle: (language: string) => Promise<Record<string, string>>,
): Promise<void> {
  if (loadedPluginLanguages.has(language)) {
    return;
  }

  const bundle = await fetchBundle(language);

  loadedPluginLanguages.add(language);
  i18n.addResourceBundle(language, 'translation', bundle, true, true);
}

/// Forgets what has been loaded, so the next sign-in fetches the catalogues again. A different
/// user may be looking at a server whose plugins have changed since.
export function resetPluginTranslations() {
  loadedPluginLanguages.clear();
}

export default i18n;
